import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone, MessageCircle } from "lucide-react";
import { NAV_LINKS, CONTACT } from "../../data/site";

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled ? "border-b border-white/10 bg-void/85 backdrop-blur-xl" : "bg-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 sm:px-8">
        <Link to="/" data-testid="brand-logo" className="group flex flex-col leading-none">
          <span className="font-display text-lg font-extrabold uppercase tracking-tight text-white">
            Shop Front <span className="text-amber">Makers</span>
          </span>
          <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-white/40">
            Irish Craft & Fabrication
          </span>
        </Link>

        <nav className="hidden items-center gap-1 lg:flex" aria-label="Primary">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.id}
              to={l.href}
              data-testid={l.id}
              className={({ isActive }) =>
                `rounded-full px-4 py-2 font-mono text-xs uppercase tracking-[0.18em] transition-colors ${
                  isActive ? "text-amber" : "text-white/60 hover:text-white"
                }`
              }
              end={l.href === "/"}
            >
              {l.name}
            </NavLink>
          ))}
        </nav>

        <div className="hidden items-center gap-3 lg:flex">
          <a
            href={CONTACT.phoneHref}
            data-testid="nav-phone-btn"
            className="flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 font-mono text-xs uppercase tracking-widest text-white/80 transition-colors hover:border-amber hover:text-amber"
            aria-label="Call the workshop"
          >
            <Phone size={14} /> Call
          </a>
          <Link
            to="/contact"
            data-testid="nav-quote-cta"
            className="rounded-full bg-amber px-5 py-2.5 font-display text-sm font-bold uppercase tracking-wide text-void transition-transform hover:scale-[1.03] hover:bg-amber-glow"
          >
            Get a Free Quote
          </Link>
        </div>

        <button
          className="lg:hidden text-white"
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden border-t border-white/10 bg-void/95 backdrop-blur-xl lg:hidden"
            data-testid="mobile-menu"
          >
            <nav className="flex flex-col gap-1 px-5 py-4">
              {NAV_LINKS.map((l) => (
                <NavLink
                  key={l.id}
                  to={l.href}
                  end={l.href === "/"}
                  className="rounded-lg px-4 py-3 font-display text-lg font-semibold uppercase text-white/80"
                >
                  {l.name}
                </NavLink>
              ))}
              <div className="mt-3 flex gap-3">
                <a href={CONTACT.phoneHref} className="flex flex-1 items-center justify-center gap-2 rounded-full border border-white/15 py-3 font-mono text-xs uppercase text-white">
                  <Phone size={16} /> Call
                </a>
                <a href={CONTACT.whatsappHref} target="_blank" rel="noreferrer" className="flex flex-1 items-center justify-center gap-2 rounded-full border border-white/15 py-3 font-mono text-xs uppercase text-white">
                  <MessageCircle size={16} /> WhatsApp
                </a>
              </div>
              <Link to="/contact" className="mt-3 rounded-full bg-amber py-3 text-center font-display font-bold uppercase text-void">
                Get a Free Quote
              </Link>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
